import React, { type ReactNode } from "react";
import { Box, Text, Flex, Button, useToast, type FlexProps, useClipboard } from "@chakra-ui/react";
import { ClipboardIcon, LinkedinIcon, XIcon } from "ui/icons";
import { Link } from "ui/link";
import { Tooltip } from "ui/tooltip";
import { SidebarDemo } from "./sidebar-demo";

type SidebarShareProps = {
  /** Relative path to the current page, without the leading `/`. */
  path: string;
  cta?: ReactNode;
};

export function SidebarShare({ path, cta }: SidebarShareProps) {
  const value = `https://hightouch.com/${path}`;
  const { onCopy } = useClipboard(value);
  const toast = useToast();

  return (
    <>
      <Box>
        <Text ml="tiny" mb="smaller" textStyle="overline1" fontWeight="semibold">
          Share
        </Text>

        <Flex align="center" gap={6} ml="tiny">
          <Tooltip message="Share on LinkedIn" placement="top">
            <ShareLink
              aria-label="Share on LinkedIn"
              href={`https://www.linkedin.com/sharing/share-offsite/?url=https://hightouch.com/${path}`}
            >
              <LinkedinIcon />
            </ShareLink>
          </Tooltip>

          <Tooltip message="Share on X" placement="top">
            <ShareLink aria-label="Share on X" href={`https://x.com/intent/tweet?url=https://hightouch.com/${path}`}>
              <XIcon />
            </ShareLink>
          </Tooltip>

          <Tooltip message="Copy url to clipboard" placement="top">
            <Button
              aria-label="Copy link to clipboard"
              display="flex"
              justifyContent="flex-start"
              variant="unstyled"
              boxSize="smaller"
              minW="auto"
              color="brand.accent.compliment"
              transition="150ms all"
              _hover={{ color: "brand.accent.compliment" }}
              onClick={() => {
                onCopy();
                toast({
                  title: `Link copied to clipboard.`,
                  status: "success",
                  isClosable: true,
                });
              }}
            >
              <ClipboardIcon boxSize="smaller" mt="tiniest" />
            </Button>
          </Tooltip>
        </Flex>
      </Box>

      {cta ?? <SidebarDemo />}
    </>
  );
}

type ShareLinkProps = {
  href: string;
} & FlexProps;

function ShareLink({ href, children, ...props }: ShareLinkProps) {
  return (
    <Flex
      as={Link}
      target="_blank"
      href={href}
      rel="noreferrer"
      align="start"
      boxSize="smaller"
      justify="start"
      color="brand.accent.compliment"
      transition="150ms all"
      _hover={{ color: "brand.accent.compliment" }}
      {...props}
    >
      {children}
    </Flex>
  );
}
